import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { dispatch } from '@ngxs/store';
import type { Card } from '../../interfaces/ICardItem';
import { TileService } from '../../services/tile.service';
import { FormsModule } from '@angular/forms';
import { SiFormItemComponent } from '@siemens/element-ng/form';
import { SiIconComponent } from '@siemens/element-ng/icon';
import { ModalRef } from '@siemens/element-ng/modal';
import type { EditTileDialogData } from '../../interfaces/edit-tile-dialog.type';
import { AddReport, DeleteReport, UpdateReport } from '../../state/reports/reports.actions';
import { AddSalData, DeleteSalData, UpdateSalData } from '../../state/sal-data/sal-data.actions';

@Component({
    selector: 'app-edit-tile-dialog',
    templateUrl: './edit-tile-dialog.component.html',
    styleUrls: ['./edit-tile-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SiFormItemComponent, SiIconComponent, FormsModule, TranslatePipe]
})
export class EditTileDialogComponent implements OnInit {
    protected readonly data = input<EditTileDialogData>({ mode: 'new' });

    private readonly tileService = inject(TileService);
    private readonly translateService = inject(TranslateService);
    private readonly modalRef = inject<ModalRef<unknown, string>>(ModalRef);

    private readonly addReport = dispatch(AddReport);
    private readonly updateReport = dispatch(UpdateReport);
    private readonly deleteReport = dispatch(DeleteReport);
    private readonly addSalData = dispatch(AddSalData);
    private readonly updateSalData = dispatch(UpdateSalData);
    private readonly deleteSalData = dispatch(DeleteSalData);

    protected readonly currentTile = signal<Partial<Card>>({});
    protected readonly isSubmitting = signal<boolean>(false);
    protected readonly isEditMode = computed(() => this.data().mode === 'edit');
    protected readonly dialogTitleKey = computed(() => `tileDialog.title.${this.data().mode}`);
    protected readonly isSaveDisabled = computed(() => {
        const tile = this.currentTile();
        const missingTitle = !tile.title;
        const canDownloadNotSet = tile.canDownload === undefined;
        const missingDocumentKey = tile.canDownload && !tile.key;
        return this.isSubmitting() || missingTitle || canDownloadNotSet || missingDocumentKey;
    });

    public ngOnInit(): void {
        this.initCurrentTile();
    }

    protected close(): void {
        this.modalRef.hide('close');
    }

    protected async deleteTile(): Promise<void> {
        const proceed = confirm('Delete?');
        if (!proceed) {
            return;
        }

        this.isSubmitting.set(true);
        try {
            const tile = this.currentTile();
            if (!tile.id || !tile.type) {
                throw new Error('Cannot delete card without id and type');
            }
            await this.tileService.deleteTile(tile as Card);

            // Dispatch delete action to update store
            if (tile.type === 'reports') {
                void this.deleteReport({ id: tile.id });
            } else {
                void this.deleteSalData({ id: tile.id });
            }

            this.modalRef.hide('deleted');
        } finally {
            this.isSubmitting.set(false);
        }
    }

    protected async save(): Promise<void> {
        if (this.isSaveDisabled()) {
            return;
        }

        this.isSubmitting.set(true);
        try {
            const tile = this.currentTile();
            if (tile.id) {
                const result = await this.tileService.saveTile(tile as Card);
                this.currentTile.set(result);

                // Dispatch update action to update store
                if (result.type === 'reports') {
                    void this.updateReport({ report: result });
                } else {
                    void this.updateSalData({ data: result });
                }

                this.modalRef.hide('saved');
                return;
            }

            const tileToCreate: Partial<Card> = {
                ...tile,
                language: this.translateService.getCurrentLang() ?? undefined
            };
            const result = await this.tileService.createTile(tileToCreate);
            this.currentTile.set(result);

            // Dispatch add action to update store
            if (result.type === 'reports') {
                void this.addReport({ report: result });
            } else {
                void this.addSalData({ data: result });
            }

            this.modalRef.hide('created');
        } finally {
            this.isSubmitting.set(false);
        }
    }

    protected updateTitle(value: string | null): void {
        this.patchTile({ title: value || '' });
    }

    protected updateKey(value: string | null): void {
        this.patchTile({ key: value ?? undefined });
    }

    protected updateCanDownload(value: boolean | null): void {
        const canDownload = value ?? false;
        // Clear key when switching to info mode
        if (!canDownload) {
            this.currentTile.update(tile => ({ ...tile, canDownload, key: undefined }));
        } else {
            this.patchTile({ canDownload });
        }
    }

    protected updateContent(value: string | null): void {
        this.patchTile({ content: value || '' });
    }

    private initCurrentTile(): void {
        const dialogData = this.data();
        const initialTile: Partial<Card> = {
            title: '',
            content: '',
            canDownload: false,
            ...dialogData.tileData
        };

        if (dialogData.mode === 'new' && dialogData.type) {
            initialTile.type = dialogData.type;
        }

        this.currentTile.set(initialTile);
    }

    private patchTile(patch: Partial<Card>): void {
        this.currentTile.update(currentTile => ({
            ...currentTile,
            ...patch
        }));
    }
}
